import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'
import type { ReviewDecision } from './ReportEntry'
import {
  parseRegistryLink,
  parseRevisionDates,
  parseSourceLink,
  toConfidenceTier,
  toRevisionStatus,
  type ConfidenceTier,
  type RegistryLink,
  type RevisionDates,
  type RevisionStatus,
  type SourceLink,
  parseIdentityRejection,
  precisionOfRevisionDate,
  type DatePrecision,
  type IdentityRejection,
} from './registry'

/**
 * v2 report entry vocabulary (MYAC-904,
 * work/2026-07-29-regmon-v2-report/plan.md Phase 1/4). This is a SEPARATE
 * model from the legacy `ReportEntry` — the report view branches once on the
 * report doc's `schema_version` and picks one of the two entry models, it
 * never mixes fields from both on a single card.
 */

/** `unchanged` is v2-only (MYAC-861): a family with zero change events in the
 *  reference window gets a real PERSISTED entry here — the front stops
 *  re-deriving "No changes" as `list - entries` by designation string. */
/** MYAC-904 re-partition: `unrecognised` (the pinned edition matches nothing
 *  in the catalogue) and `adopted` (an EN/regional adoption of the same text
 *  exists, with its own dates of application) answer questions the windowed
 *  categories structurally cannot. Unknown values still degrade to
 *  `'unknown'` — a front deployed before a backend that emits a newer
 *  category must never crash on it. */
export type ReportEntryCategoryV2 =
  | 'withdrawn'
  | 'published'
  | 'upcoming'
  | 'unchanged'
  | 'unrecognised'
  | 'adopted'
  | 'unknown'
const REPORT_ENTRY_CATEGORIES_V2: ReadonlySet<string> = new Set([
  'withdrawn',
  'published',
  'upcoming',
  'unchanged',
  'unrecognised',
  'adopted',
])
export function toReportEntryCategoryV2(v: unknown): ReportEntryCategoryV2 {
  return typeof v === 'string' && REPORT_ENTRY_CATEGORIES_V2.has(v) ? (v as ReportEntryCategoryV2) : 'unknown'
}

/** Whether the row resolved to a live registry family (spec invariant #3:
 *  "every row appears in the report" — an unresolved row gets an explicit
 *  `in_progress` entry, never a drop). NOTE: the CMS model does not yet
 *  carry an `unverifiable` value or a `suggested_families` snapshot — see
 *  the did-you-mean dependency-gap note in the Phase 4 delivery report. */
export type ReportEntryIdentification = 'identified' | 'in_progress' | 'not_found' | 'needs_review' | 'unknown'
const IDENTIFICATIONS: ReadonlySet<string> = new Set(['identified', 'in_progress', 'not_found', 'needs_review'])
export function toReportEntryIdentification(v: unknown): ReportEntryIdentification {
  return typeof v === 'string' && IDENTIFICATIONS.has(v) ? (v as ReportEntryIdentification) : 'unknown'
}

/** Deterministic change vocabulary — derived from revision dates + family
 *  changelog, never guessed by an LLM. Rendered as a canned i18n string per
 *  kind (+ optional `detail` supplement). */
export type ReportChangeKind =
  | 'revision_published'
  | 'entered_into_force'
  | 'transition_ending'
  | 'superseded'
  | 'harmonization_change'
  | 'withdrawn'
  | 'unknown'
const CHANGE_KINDS: ReadonlySet<string> = new Set([
  'revision_published',
  'entered_into_force',
  'transition_ending',
  'superseded',
  'harmonization_change',
  'withdrawn',
])
export function toReportChangeKind(v: unknown): ReportChangeKind {
  return typeof v === 'string' && CHANGE_KINDS.has(v) ? (v as ReportChangeKind) : 'unknown'
}

export interface ReportChange {
  kind: ReportChangeKind
  date: Date | null
  revision_key: string | null
  detail: string | null
}

function parseReportChange(raw: unknown): ReportChange | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  return {
    kind: toReportChangeKind(r.kind),
    date: anyToDate({ obj: r, key: 'date' }) ?? null,
    revision_key: typeof r.revision_key === 'string' ? r.revision_key : null,
    detail: typeof r.detail === 'string' ? r.detail : null,
  }
}

function parseReportChanges(x: { obj: Record<string, unknown>; key: string }): ReportChange[] {
  const raw = x.obj[x.key]
  if (!Array.isArray(raw)) return []
  return raw.map(parseReportChange).filter((c): c is ReportChange => c !== null)
}

export interface ReportEntryRevisionFacts {
  status: RevisionStatus
  dates: RevisionDates
  harmonized: boolean | null
  oj_cessation_date: Date | null
  successor: RegistryLink | null
  source_links: SourceLink[]
  adoption_labels: Record<string, string> | null
  /** MYAC-904 report-v2 polish (defect #3) — additive fields from the
   *  parallel data-side branch identifying WHICH revision this entry is:
   *  `revision_key` is the machine key ("2026"), `revision_label` an
   *  optional curated human label ("2026 edition"). Both are tolerated as
   *  absent (old fixture data / not-yet-migrated families) — the front
   *  falls back to `revision_key` (or nothing) when `revision_label` is
   *  missing, never crashes. */
  revision_key: string | null
  revision_label: string | null
}

/**
 * The precision of a change's date (D75). A change's date is a copy of one of
 * the dates of the revision named by its `revision_key`; when that is the
 * entry's own revision, its `dates.precision` says how precise the copy is.
 * Another revision's date (the "newer revision than subscribed" change) has no
 * precision on this entry and keeps the day format, as before D75.
 */
export function reportChangeDatePrecision(
  change: Pick<ReportChange, 'date' | 'revision_key'>,
  revision: Pick<ReportEntryRevisionFacts, 'dates' | 'revision_key'> | null,
): DatePrecision {
  if (!change.date || !revision?.revision_key || change.revision_key !== revision.revision_key) return 'day'
  return precisionOfRevisionDate(revision.dates, change.date)
}

/**
 * The precision of an entry's `parsed_date` (D75): the backend writes the
 * calendar day (YYYY-MM-DD) of its most recent dated change, so the precision
 * is that change's. `day` when no change of the entry's own revision carries
 * that day.
 */
export function parsedDatePrecision(
  parsedDate: string | null,
  changes: readonly Pick<ReportChange, 'date' | 'revision_key'>[],
  revision: Pick<ReportEntryRevisionFacts, 'dates' | 'revision_key'> | null,
): DatePrecision {
  if (!parsedDate) return 'day'
  const day = parsedDate.slice(0, 10)
  const matching = changes.filter((c) => c.date && c.date.toISOString().slice(0, 10) === day)
  const precisions = matching.map((c) => reportChangeDatePrecision(c, revision))
  // Finest first, as `precisionOfRevisionDate` ranks them.
  for (const p of ['day', 'unknown', 'month', 'year'] as const) {
    if (precisions.includes(p)) return p
  }
  return 'day'
}

export function parseRevisionFacts(x: { obj: Record<string, unknown>; key: string }): ReportEntryRevisionFacts | null {
  const raw = x.obj[x.key]
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const sourceLinksRaw = Array.isArray(r.source_links) ? r.source_links : []
  const adoptionLabels =
    r.adoption_labels && typeof r.adoption_labels === 'object'
      ? (r.adoption_labels as Record<string, string>)
      : null
  return {
    status: toRevisionStatus(r.status),
    dates: parseRevisionDates(r.dates),
    harmonized: typeof r.harmonized === 'boolean' ? r.harmonized : null,
    oj_cessation_date: anyToDate({ obj: r, key: 'oj_cessation_date' }) ?? null,
    successor: parseRegistryLink(r.successor),
    source_links: sourceLinksRaw
      .map(parseSourceLink)
      .filter((s): s is SourceLink => s !== null),
    adoption_labels: adoptionLabels,
    revision_key: typeof r.revision_key === 'string' && r.revision_key ? r.revision_key : null,
    revision_label: typeof r.revision_label === 'string' && r.revision_label ? r.revision_label : null,
  }
}

@Exclude()
export class ReportEntryV2 extends _FirebaseDocument {
  @Expose()
  @Transform(({ obj, key }) => parseRegistryLink(obj[key]))
  public registry_ref: RegistryLink | null = null

  /** Plural on purpose: entries are per FAMILY, deduped across every
   *  subscriber row that resolves to it (regulation_lists doc IDs).
   *  Explicit `@Transform` (unlike a bare `@Expose()`): class-transformer
   *  overwrites the `= []` default with `undefined` when the source plain
   *  object omits the key at all — this guarantees `.includes()` callers
   *  (deep-link matching, review-writes-all-rows) never see `undefined`. */
  @Expose()
  @Transform(({ obj, key }) => (Array.isArray(obj[key]) ? obj[key] : []))
  public subscription_row_ids: string[] = []

  @Expose()
  @Transform(({ obj, key }) => toReportEntryIdentification(obj[key]))
  public identification: ReportEntryIdentification = 'unknown'

  @Expose()
  @Transform(({ obj, key }) => toReportEntryCategoryV2(obj[key]))
  public category: ReportEntryCategoryV2 = 'unknown'

  @Expose()
  @Transform(({ obj, key }) => toConfidenceTier(obj[key]))
  public tier: ConfidenceTier = 'unknown'

  @Expose()
  @Transform(anyToDate)
  public last_verified: Date | null = null

  /** Past-TTL curated family served anyway, with `last_verified` surfaced
   *  (spec invariant #4: staleness degrades, never destroys). */
  @Expose()
  public stale: boolean = false

  /** MYAC-904 report-v2 polish (defect #3) — additive: the subscription row
   *  didn't pin an exact revision, so this entry shows the family's latest
   *  KNOWN revision instead of a confirmed subscribed one. Absent on old
   *  fixture data — defaults to `false` (never shown) rather than crashing
   *  or defaulting to "unconfirmed" by omission.
   *  Explicit `@Transform` (same reason as `subscription_row_ids` above):
   *  class-transformer overwrites the `= false` default with `undefined`
   *  when the source plain object omits the key entirely. */
  @Expose()
  @Transform(({ obj, key }) => obj[key] === true)
  public subscribed_revision_unconfirmed: boolean = false

  /** Demand lifecycle (MYAC-904, 2026-08-11): the curator's researched note
   *  when the pinned revision key was REJECTED as a nonexistent edition
   *  (family `rejected_identifiers`) — the unrecognised strip then gives the
   *  registry's answer instead of "still being verified". Additive/tolerant:
   *  absent on entries written before the field existed. */
  @Expose()
  @Transform(({ obj, key }) => (typeof obj[key] === 'string' && obj[key] ? obj[key] : null))
  public pinned_revision_rejected_note: string | null = null

  /** Identity-level rejection (MYAC-904, 2026-08-12): projection of the
   *  family's terminal "this designation is not a trackable instrument"
   *  answer ({kind, note, suggestions}) — the card renders it instead of
   *  the eternal "awaiting verification". Additive/tolerant: absent on
   *  entries written before the field existed. */
  @Expose()
  @Transform(({ obj, key }) => parseIdentityRejection(obj[key]))
  public family_rejection: IdentityRejection | null = null

  @Expose()
  @Transform(parseRevisionFacts)
  public revision: ReportEntryRevisionFacts | null = null

  @Expose()
  @Transform(parseReportChanges)
  public changes: ReportChange[] = []

  // Display fields — same names/semantics as legacy `ReportEntry` so the
  // review dialog and shared bits don't need a rename.
  @Expose()
  public designation: string = ''

  @Expose()
  public badge: string = ''

  @Expose()
  public description: string = ''

  @Expose()
  public reference_url: string | null = null

  @Expose()
  public parsed_date: string | null = null

  @Expose()
  public replaced_by: string = ''

  @Expose()
  public review_decision: ReviewDecision = 'pending'

  @Expose()
  public review_comment: string = ''

  @Expose()
  public reviewed_by: string = ''

  @Expose()
  @Transform(anyToDate)
  public reviewed_at: Date | null = null

  collection(): string {
    return 'entries'
  }

  static fromFirebase(o: DocumentSnapshot): ReportEntryV2 | null {
    return _FirebaseDocument._fromFirebase(ReportEntryV2, o)
  }
}
